import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { useCookieConsentContext } from "@/providers/CookieConsentProvider";
import { useFeedbackConfig } from "@/providers/FeedbackConfigProvider";
import { ANALYTICS_POLICY } from "@/types/cookieConsent";

declare global {
  interface Window {
    oc?: { renderUnloadedComponents?: () => void };
  }
}

interface FeatureFeedbackProps {
  feature: string;
  product?: string;
  theme?: "light" | "dark";
  dismissible?: boolean;
  language?: string;
}

const loadedScripts = new Set<string>();

function loadOcClient(registryBase: string): Promise<void> {
  const src = `${registryBase}/oc-client/client.js`;
  if (loadedScripts.has(src) || window.oc) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      loadedScripts.add(src);
      resolve();
    };
    script.onerror = () =>
      reject(new Error(`Failed to load OC client from ${src}`));
    document.head.appendChild(script);
  });
}

export function FeatureFeedback({
  feature,
  product = "Cash Forecaster",
  theme = "light",
  dismissible = true,
  language = "en-GB",
}: FeatureFeedbackProps) {
  const { consent } = useCookieConsentContext();
  const { evoUserInfo } = useAuth();
  const feedbackConfig = useFeedbackConfig();

  const analyticsGranted = consent?.isGranted(ANALYTICS_POLICY) ?? false;
  const resolvedUserId = evoUserInfo?.userId ?? "";
  const resolvedCustomerName = evoUserInfo?.customerName ?? "";
  const containerRef = useRef<HTMLDivElement>(null);

  const mountComponent = useCallback(async () => {
    const container = containerRef.current;
    if (!container || !feedbackConfig) return;

    try {
      await loadOcClient(feedbackConfig.mfeRegistryBase);
    } catch (e) {
      console.error("[FeatureFeedback]", e);
      return;
    }

    const params = new URLSearchParams({
      feature,
      product,
      accountToken: feedbackConfig.accountToken,
      apiHost: feedbackConfig.apiHost,
      customerName: resolvedCustomerName,
      userId: resolvedUserId,
      theme,
      reDisplayAfterDays: feedbackConfig.reDisplayAfterDays.toString(),
      dismissible: dismissible.toString(),
      language,
    });

    const version = feedbackConfig.mfeVersion ?? "1";
    const componentUrl = `${feedbackConfig.mfeRegistryBase}/evo-feature-feedback-collector/${version}/?${params}`;

    const ocComponent = document.createElement("oc-component");
    ocComponent.setAttribute("href", componentUrl);

    container.innerHTML = "";
    container.appendChild(ocComponent);

    window.oc?.renderUnloadedComponents?.();
  }, [
    feature,
    product,
    feedbackConfig,
    resolvedCustomerName,
    resolvedUserId,
    theme,
    dismissible,
    language,
  ]);

  useEffect(() => {
    if (!feedbackConfig || !analyticsGranted) return;

    mountComponent();

    return () => {
      const container = containerRef.current;
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [feedbackConfig, analyticsGranted, mountComponent]);

  if (!feedbackConfig || !analyticsGranted) {
    return null;
  }

  return <div ref={containerRef} />;
}
